import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../../../supabaseClient';
import {
  Grid,
  TextField,
  Typography,
  Box,
  alpha,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  Chip,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Gavel as GavelIcon,
  SaveAs as SaveAsIcon,
  FolderOpen as FolderOpenIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { SectionPaper, SectionTitle } from './styledComponents';

const TermsConditionsSection = ({ bol, onUpdate, canEdit = true, canDelete = true }) => {
  const theme = useTheme();
  const { companyId } = useParams();
  const [templates, setTemplates] = useState([]);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const defaultTerms = `STANDARD TERMS AND CONDITIONS:

1. CARRIER LIABILITY: The carrier's liability is limited to the actual value of the goods lost or damaged, not to exceed the declared value.

2. CLAIMS: All claims must be filed in writing within 9 months of delivery date.

3. INSPECTION: Consignee must inspect goods immediately upon delivery and note any visible damage on the delivery receipt.

4. FREIGHT CHARGES: Freight charges are payable by the party designated in the "Freight Terms" section.

5. DELIVERY: Carrier will make reasonable efforts to deliver by the expected delivery date but is not liable for delays beyond their control.

6. SIGNATURE: By signing this Bill of Lading, the shipper and consignee agree to all terms and conditions stated herein.`;

  // Load templates
  useEffect(() => {
    if (companyId) {
      fetchTemplates();
    }
  }, [companyId]);

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('terms_templates')
        .select('*')
        .eq('company_id', companyId)
        .in('template_type', ['bol', 'both'])
        .order('template_name');

      if (error) throw error;
      setTemplates(data || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const handleLoadTemplate = (template) => {
    onUpdate({ terms_conditions: template.terms_content });
    setLoadDialogOpen(false);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      setSnackbar({ open: true, message: 'Please enter a template name', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('terms_templates')
        .insert([{
          company_id: companyId,
          template_name: templateName.trim(),
          template_type: 'both',
          terms_content: bol.terms_conditions || '',
          created_by: user?.id
        }]);

      if (error) throw error;

      await fetchTemplates();
      setTemplateName('');
      setSaveDialogOpen(false);
      setSnackbar({ open: true, message: 'Template saved successfully!', severity: 'success' });
    } catch (err) {
      console.error('Error saving template:', err);
      setSnackbar({ open: true, message: 'Failed to save template: ' + err.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate({ 
      ...template, 
      editedName: template.template_name,
      editedContent: template.terms_content 
    });
    setEditDialogOpen(true);
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate?.editedName?.trim()) {
      setSnackbar({ open: true, message: 'Please enter a template name', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('terms_templates')
        .update({
          template_name: editingTemplate.editedName.trim(),
          terms_content: editingTemplate.editedContent,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingTemplate.id);

      if (error) throw error;

      await fetchTemplates();
      setEditingTemplate(null);
      setEditDialogOpen(false);
      setSnackbar({ open: true, message: 'Template updated successfully!', severity: 'success' });
    } catch (err) {
      console.error('Error updating template:', err);
      setSnackbar({ open: true, message: 'Failed to update template: ' + err.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (template) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;

    try {
      const { error } = await supabase
        .from('terms_templates')
        .delete()
        .eq('id', templateToDelete.id);

      if (error) throw error;
      await fetchTemplates();
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      setSnackbar({ open: true, message: 'Template deleted successfully!', severity: 'success' });
    } catch (err) {
      console.error('Error deleting template:', err);
      setSnackbar({ open: true, message: 'Failed to delete template: ' + err.message, severity: 'error' });
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  return (
    <SectionPaper sx={{ width: '100%', mt: 3 }}>
      <SectionTitle variant="h6">
        <GavelIcon />
        Terms & Conditions
      </SectionTitle>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ 
            p: 2, 
            bgcolor: alpha(theme.palette.warning.main, 0.05), 
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            mb: 2
          }}>
            <Typography variant="caption" sx={{ fontWeight: 500, color: 'warning.main' }}>
              Important: These terms govern the shipment. Modify as needed for your business requirements.
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<FolderOpenIcon />}
              onClick={() => canEdit && setLoadDialogOpen(true)}
              sx={{ 
                borderRadius: 2,
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'pointer' : 'not-allowed'
              }}
            >
              Load Template
            </Button>
            <Button
              variant="outlined"
              startIcon={<SaveAsIcon />}
              onClick={() => canEdit && setSaveDialogOpen(true)}
              sx={{ 
                borderRadius: 2,
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'pointer' : 'not-allowed'
              }}
            >
              Save as Template
            </Button>
          </Box>

          <TextField
            label="Terms and Conditions"
            fullWidth
            multiline
            rows={30}
            value={bol.terms_conditions || ''}
            onChange={(e) => canEdit && onUpdate({ terms_conditions: e.target.value })}
            placeholder="Enter terms and conditions..."
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'text' : 'not-allowed'
              }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 2, mt: 2, fontWeight: 500, color: 'text.secondary' }}>
            Shipper/Consignor Signature
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Shipper Signature Name"
            fullWidth
            value={bol.shipper_signature_name || ''}
            onChange={(e) => canEdit && onUpdate({ shipper_signature_name: e.target.value })}
            placeholder="Printed name of shipper"
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'text' : 'not-allowed'
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Shipper Signature Title"
            fullWidth
            value={bol.shipper_signature_title || ''}
            onChange={(e) => canEdit && onUpdate({ shipper_signature_title: e.target.value })}
            placeholder="e.g., Warehouse Manager"
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'text' : 'not-allowed'
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Shipper Signature Date"
              value={bol.shipper_signature_date ? dayjs(bol.shipper_signature_date) : null}
              onChange={(newValue) => {
                canEdit && onUpdate({ 
                  shipper_signature_date: newValue ? newValue.format('YYYY-MM-DD') : '' 
                });
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  InputProps: {
                    sx: { 
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& fieldset': { border: 'none' },
                      opacity: canEdit ? 1 : 0.5,
                      pointerEvents: canEdit ? 'auto' : 'none',
                      cursor: canEdit ? 'pointer' : 'not-allowed'
                    }
                  }
                }
              }}
            />
          </LocalizationProvider>
        </Grid>

      </Grid>

      {/* Load Template Dialog */}
      <Dialog 
        open={loadDialogOpen} 
        onClose={() => setLoadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Load Terms Template</Typography>
          <IconButton onClick={() => setLoadDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {templates.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              No saved templates. Create one by clicking "Save as Template".
            </Typography>
          ) : (
            <List>
              {templates.map((template) => (
                <ListItem
                  key={template.id}
                  disablePadding
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton 
                        edge="end" 
                        onClick={() => canEdit && handleEditTemplate(template)}
                        color="primary"
                        sx={{
                          opacity: canEdit ? 1 : 0.3,
                          pointerEvents: canEdit ? 'auto' : 'none',
                          cursor: canEdit ? 'pointer' : 'not-allowed'
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        edge="end" 
                        onClick={() => canDelete && handleDeleteClick(template)}
                        color="error"
                        sx={{
                          opacity: canDelete ? 1 : 0.3,
                          pointerEvents: canDelete ? 'auto' : 'none',
                          cursor: canDelete ? 'pointer' : 'not-allowed'
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemButton onClick={() => handleLoadTemplate(template)}>
                    <ListItemText
                      primary={template.template_name}
                      secondary={`Created ${new Date(template.created_at).toLocaleDateString()}`}
                    />
                    {template.is_default && (
                      <Chip label="Default" size="small" color="primary" sx={{ ml: 1 }} />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoadDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Save Template Dialog */}
      <Dialog 
        open={saveDialogOpen} 
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save Terms Template</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Template Name"
            fullWidth
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="e.g., Standard BOL Terms, International Shipping Terms"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveTemplate} 
            variant="contained" 
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => {
          setEditDialogOpen(false);
          setEditingTemplate(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Terms Template</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Template Name"
            fullWidth
            value={editingTemplate?.editedName || ''}
            onChange={(e) => setEditingTemplate({
              ...editingTemplate,
              editedName: e.target.value
            })}
            placeholder="e.g., Standard BOL Terms, International Shipping Terms"
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            label="Template Content"
            multiline
            rows={20}
            fullWidth
            value={editingTemplate?.editedContent || ''}
            onChange={(e) => setEditingTemplate({
              ...editingTemplate,
              editedContent: e.target.value
            })}
            sx={{ 
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
                fontSize: '0.9rem'
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setEditingTemplate(null);
          }}>Cancel</Button>
          <Button 
            onClick={handleUpdateTemplate} 
            variant="contained" 
            disabled={saving}
          >
            {saving ? 'Updating...' : 'Update Template'}
          </Button>
        </DialogActions>
      </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setTemplateToDelete(null);
          }}
          maxWidth="xs"
          fullWidth
          disableScrollLock
        >
          <DialogTitle sx={{ fontWeight: 500, color: 'error.main' }}>
            Delete Template?
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{templateToDelete?.template_name}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setDeleteDialogOpen(false);
                setTemplateToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </SectionPaper>
    );
  };
  
  export default TermsConditionsSection;